import { useState, type SyntheticEvent } from "react";
import {
  clinicalApi,
  type ClinicalInput,
  type ImportResult,
  type PredictionResult,
} from "../../lib/api/clinical";
import { formField, levelClass, readFileAsBase64 } from "./format";

interface Props {
  onChanged: () => void;
}

function numberOrNull(value: string): number | null {
  const trimmed = value.trim();
  if (!trimmed) return null;
  const parsed = Number(trimmed);
  return Number.isFinite(parsed) ? parsed : null;
}

export function PredictPanel({ onChanged }: Props) {
  const [result, setResult] = useState<PredictionResult | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [importResult, setImportResult] = useState<ImportResult | null>(null);
  const [importBusy, setImportBusy] = useState(false);
  const [pendingFile, setPendingFile] = useState<File | null>(null);

  async function submit(event: SyntheticEvent<HTMLFormElement>) {
    event.preventDefault();
    setBusy(true);
    setError("");
    const form = new FormData(event.currentTarget);
    const sourceIdentifier = formField(form, "source_identifier").trim();
    const saveToWorkbench = form.get("save_to_workbench") === "on";
    if (saveToWorkbench && !sourceIdentifier) {
      setBusy(false);
      setError("保存到工作台时必须填写住院号/病历号");
      return;
    }
    const input: ClinicalInput = {
      age: numberOrNull(formField(form, "age")),
      catheter_days: numberOrNull(formField(form, "catheter_days")),
      catheter_count: numberOrNull(formField(form, "catheter_count")),
      diagnosis_text: formField(form, "diagnosis_text"),
      abx_names: formField(form, "abx_names"),
      abx_combined: form.get("abx_combined") === "on",
      albumin: numberOrNull(formField(form, "albumin")),
      creatinine: numberOrNull(formField(form, "creatinine")),
      blood_wbc: numberOrNull(formField(form, "blood_wbc")),
      neutrophil_pct: numberOrNull(formField(form, "neutrophil_pct")),
    };
    try {
      let patientId: string | undefined;
      if (saveToWorkbench) {
        const age = input.age ?? null;
        const patient = await clinicalApi.upsertPatient({
          source_identifier: sourceIdentifier,
          birth_year:
            age === null ? null : new Date().getFullYear() - Math.floor(age),
          ward: formField(form, "ward").trim() || null,
          bed: formField(form, "bed").trim() || null,
        });
        patientId = patient.id;
      }
      const prediction = await clinicalApi.predict(input, patientId, saveToWorkbench);
      setResult(prediction);
      if (saveToWorkbench) onChanged();
    } catch {
      setError("预测失败，请检查录入或稍后重试");
    } finally {
      setBusy(false);
    }
  }

  async function runImport(commit: boolean) {
    if (!pendingFile) {
      setError("请先选择 Excel 文件");
      return;
    }
    setImportBusy(true);
    setError("");
    try {
      const content = await readFileAsBase64(pendingFile);
      const outcome = await clinicalApi.importPredict(content, pendingFile.name, commit);
      setImportResult(outcome);
      if (commit) onChanged();
    } catch {
      setError("导入失败，请确认文件为标准 .xlsx 格式");
    } finally {
      setImportBusy(false);
    }
  }

  return (
    <div className="view predict-view">
      <section className="predict-form-card">
        <h2>单患者快速评估</h2>
        <p className="hint">仅需填写少量置管早期已知信息；检验缺失不影响出结果。</p>
        <form onSubmit={(event) => void submit(event)} className="predict-form">
          <label className="full">
            住院号/病历号（仅服务端加密匹配，不在页面显示）
            <input name="source_identifier" autoComplete="off" />
          </label>
          <label>
            病区<input name="ward" />
          </label>
          <label>
            床号<input name="bed" />
          </label>
          <label>
            年龄<input name="age" inputMode="numeric" />
          </label>
          <label>
            导管留置天数<input name="catheter_days" inputMode="numeric" />
          </label>
          <label>
            插管次数<input name="catheter_count" inputMode="numeric" />
          </label>
          <label className="full">
            当前诊断<textarea name="diagnosis_text" rows={2} placeholder="如：泌尿系统感染、脑梗死" />
          </label>
          <label className="full">
            抗菌药物<input name="abx_names" placeholder="如：头孢曲松；左氧氟沙星" />
          </label>
          <label className="checkbox">
            <input type="checkbox" name="abx_combined" /> 抗菌药联用
          </label>
          <label>
            白蛋白<input name="albumin" inputMode="decimal" />
          </label>
          <label>
            血肌酐<input name="creatinine" inputMode="decimal" />
          </label>
          <label>
            血白细胞<input name="blood_wbc" inputMode="decimal" />
          </label>
          <label>
            中性粒%<input name="neutrophil_pct" inputMode="decimal" />
          </label>
          <label className="checkbox full">
            <input type="checkbox" name="save_to_workbench" defaultChecked /> 保存患者、评估和处置工单
          </label>
          <button type="submit" className="primary full" disabled={busy}>
            {busy ? "评估中…" : "生成风险评估"}
          </button>
        </form>
        {error ? <p role="alert">{error}</p> : null}
      </section>

      {result ? (
        <section className={`risk-card big ${levelClass(result.level)}`}>
          <div className="risk-card-top">
            <span className="risk-tier">{result.tier_label}</span>
            <span className="risk-index">{result.risk_index}</span>
          </div>
          <p className="risk-rate">同风险层参考感染率 {result.calibrated_rate_text}</p>
          <p className="risk-action">{result.action}</p>
          {result.factors.length > 0 ? (
            <ul className="risk-factors">
              {result.factors.map((factor) => (
                <li key={factor}>{factor}</li>
              ))}
            </ul>
          ) : null}
          {result.missing_fields.length > 0 ? (
            <p className="risk-warn">
              以下检验缺失，已按训练规则估算，建议补录：{result.missing_fields.join("、")}
            </p>
          ) : null}
          {result.assessment_id ? (
            <p className="drawer-msg">已保存到今日工作台，可进入患者处置闭环。</p>
          ) : null}
          <p className="risk-disclaimer">风险预警不等同于 CAUTI 诊断，请结合临床判断。</p>
        </section>
      ) : null}

      <section className="import-card">
        <h2>Excel 批量预测</h2>
        <p className="hint">
          使用标准插管患者工作簿（含住院号、入院年龄、诊断、导管、抗菌药、检验列）。先预检，确认无误再导入。
        </p>
        <input
          type="file"
          accept=".xlsx"
          onChange={(event) => {
            setPendingFile(event.currentTarget.files?.[0] ?? null);
            setImportResult(null);
          }}
        />
        <div className="drawer-actions">
          <button type="button" disabled={importBusy} onClick={() => void runImport(false)}>
            预检（不入库）
          </button>
          <button
            type="button"
            className="primary"
            disabled={importBusy}
            onClick={() => void runImport(true)}
          >
            确认导入
          </button>
        </div>
        {importResult ? (
          <div className="import-result">
            <p>
              共 {importResult.total_rows} 行，成功评分 {importResult.scored_rows} 行，异常{" "}
              {importResult.error_rows} 行
              {importResult.committed
                ? `；新增患者 ${String(importResult.created_patients)}、评估 ${String(importResult.created_assessments)}、工单 ${String(importResult.created_workflows)}`
                : "（预检模式，未写入）"}
            </p>
            {importResult.missing_optional_columns.length > 0 ? (
              <p className="risk-warn">
                缺少可选列：{importResult.missing_optional_columns.join("、")}
              </p>
            ) : null}
            {importResult.rows.some((row) => row.error) ? (
              <ul className="plain-list">
                {importResult.rows
                  .filter((row) => row.error)
                  .slice(0, 10)
                  .map((row) => (
                    <li key={row.row_number}>
                      第 {row.row_number} 行：{row.error}
                    </li>
                  ))}
              </ul>
            ) : null}
          </div>
        ) : null}
      </section>
    </div>
  );
}
